import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router } from '@angular/router';

import { reportMenuItems } from '../report-menu-types';
import { ReportCategory, ReportMenuEvent, ReportSubCategory } from '../reports.types';
import { environment } from 'src/environments/environment';
import { FavoritesService } from '../favorites.service';

@Component({
  selector: 'report-menu',
  templateUrl: './report-menu.component.html',
  styleUrls: ['./report-menu.component.css']
})
export class ReportMenuComponent implements OnInit, OnChanges, AfterViewInit {
  @Input({ required: true }) category!: ReportCategory;
  @Input({ required: true }) subCategory!: ReportSubCategory;

  @Output() onMenuClick = new EventEmitter<ReportMenuEvent>();

  items: MenuItem[] = [];

  constructor(private favoritesService: FavoritesService, private router: Router) {}

  ngOnInit(): void {
    this.favoritesService.favorites$.subscribe(() => {
      this.getMenuItems();
    });
  }

  ngAfterViewInit(): void {
    (window as any).toggleFav = (category: string, subCategory: string, label: string) => {
      console.log('toggleFav called:', category, subCategory, label);
      this.favoritesService.toggleFavorite(category as ReportCategory, subCategory as ReportSubCategory, label);
    };
    (window as any).navigateToReport = (category: string, subCategory: string) => {
      this.onMenuClick.emit({ category: category as ReportCategory, subCategory: subCategory as ReportSubCategory });
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    this.getMenuItems();
  }

  get isProdEnv() {
    const deployedEnv = environment.envVar.DEPLOYED_ENV_NAME.toLowerCase();
    // console.log(deployedEnv);
    return deployedEnv === 'production';
  }

  // Fn to create Menu Items required as per component
  private getMenuItems = () => {
    const favorites = this.favoritesService.favorites;
    const favoriteItems = favorites.length > 0 ? [{
      label: 'Favourite Reports',
      category: ReportCategory.FAVORITES,
      items: favorites
    }] : [];
    
    const allItems = [...favoriteItems, ...reportMenuItems];

    this.items = allItems.map((report) => {
      const obj: MenuItem = {};
      obj.id = report.category;
      obj.label = report.label;
      obj.items = report.items.map((item) => {
        const { label, category, subCategory } = item;
        const hideInProd = 'hideInProd' in item ? item.hideInProd : false;
        const isActive = this.category === category && this.subCategory === subCategory;
        const isFavorite = this.favoritesService.isFavorite(category, subCategory);
        const starClass = isActive && isFavorite ? 'pi-star-fill active-star' : isFavorite ? 'pi-star-fill favorite-star' : 'pi-star';
        
        const itemObj: MenuItem = {
          id: `${category}_${subCategory}`,
          label: label,
          visible: this.isProdEnv ? !hideInProd : true
        };
        itemObj.styleClass = this.isActive(itemObj) ? 'active-report' : '';
        return itemObj;
      });
      obj.expanded = this.isExpanded(obj);
      return obj;
    });
  };

  private isExpanded = (item: MenuItem) => item.id === this.category || item.id === ReportCategory.FAVORITES;
  private isActive = (item: MenuItem) => item.id === `${this.category}_${this.subCategory}`;

  toggleSection(sectionId: string): void {
    this.items = this.items.map(item => ({
      ...item,
      expanded: item.id === sectionId ? !item.expanded : item.expanded
    }));
  }

  navigateToReport(itemId: string): void {
    const [category, subCategory] = itemId.split('_');
    this.router.navigate([`/reports/${category}/${subCategory}`], {
      queryParamsHandling: 'preserve'
    });
    this.onMenuClick.emit({ category: category as ReportCategory, subCategory: subCategory as ReportSubCategory });
  }

  toggleFavorite(itemId: string, label: string): void {
    const [category, subCategory] = itemId.split('_');
    console.log('toggleFavorite called:', category, subCategory, label);
    this.favoritesService.toggleFavorite(category as ReportCategory, subCategory as ReportSubCategory, label);
  }

  getItemLabel(item: any): string {
    return item.label || '';
  }

  isFavoriteSection(sectionId: string): boolean {
    return sectionId === ReportCategory.FAVORITES;
  }

  getStarClass(itemId: string): string {
    const [category, subCategory] = itemId.split('_');
    const isActive = this.category === category && this.subCategory === subCategory;
    const isFavorite = this.favoritesService.isFavorite(category as ReportCategory, subCategory as ReportSubCategory);
    
    // For items in favorites section, always show filled star
    const currentSection = this.items.find(section => 
      section.items?.some(item => item.id === itemId)
    );
    
    if (currentSection?.id === ReportCategory.FAVORITES) {
      return isActive ? 'pi-star-fill active-star' : 'pi-star-fill favorite-star';
    }
    
    return isActive && isFavorite ? 'pi-star-fill active-star' : isFavorite ? 'pi-star-fill favorite-star' : 'pi-star';
  }


}
